import Vue from "vue";
import _ from "lodash";
import StoreModule from "./store_module";
import TFMService from "@/api/service";

const name = "device-manager";

const mutations = {
	LOAD_DEVICE_TYPES: (state, payload) => {
		const deviceTypes = _.reduce(
			payload,
			(acc, v, k) => {
				return Object.assign(acc, {
					[k]: Object.assign({}, v, {
						id: k,
						expanded: false, // in sidebar
						filterDeviceType: "Optional", // Radio buttons for device type default optional
						filter: null,
						loading: false
					})
				});
			},
			{}
		);
		Vue.set(state, "deviceTypes", deviceTypes);
	},

	SET_FILTER: (state, payload) => (state.filter = payload),

	CLEAR_FILTER: state => (state.filter = ""),

	TOGGLE_EXPAND_DEVICE_TYPE: (state, deviceTypeId) =>
		(state.deviceTypes[deviceTypeId].expanded = !state.deviceTypes[deviceTypeId]
			.expanded),

	SET_EXPAND_DEVICE_TYPE: (state, { deviceTypeId, expanded }) =>
		(state.deviceTypes[deviceTypeId].expanded = expanded),

	SET_DEVICE_TYPE_LOADING: (state, { deviceTypeId, isLoading }) =>
		(state.deviceTypes[deviceTypeId].loading = isLoading),

	SET_SHOW_DEVICE_TYPE: (state, { deviceTypeId, filter }) => {
		state.deviceTypes[deviceTypeId].filterDeviceType = filter;
	},

	CLEAR_ACTIVE_DEVICE: state => {
		state.activeDeviceId = null;
		state.activeDeviceTypeId = null;
	},

	SET_ACTIVE_DEVICE: (state, { deviceId, $deviceTypeId }) => {
		if (deviceId === state.activeDeviceId) {
			// deselect if the same device is selected again
			deviceId = null;
			$deviceTypeId = null;
		}
		state.activeDeviceId = deviceId;
		state.activeDeviceTypeId = $deviceTypeId;
	},

	SET_CONTEXT_MENU_LAT_LNG: (state, payload) =>
		(state.contextMenuLatLng = payload)
};

const actions = {
	async fetchDeviceTypes(context) {
		const deviceTypes = await TFMService.getDeviceTypes();
		context.commit("LOAD_DEVICE_TYPES", deviceTypes);
	},

	async fetchDefaultDevices(context) {
		const ddts = context.getters["defaultDeviceTypesToLoad"];
		let promises = [];
		for (const id in ddts) {
			if (ddts[id]) {
				promises.push(context.dispatch("fetchDevicesOfType", id));
			}
		}
		return Promise.all(promises);
	},

	async fetchDeviceMetadata(context, { deviceId, $deviceTypeId }) {
		return await context.dispatch(
			context.getters.deviceTypeModuleName($deviceTypeId) + "/FETCH_METADATA",
			{ id: deviceId },
			{ root: true }
		);
	},

	async fetchDevicesOfType(context, type) {
		if (!context.getters.doesDeviceTypeHaveModule(type)) return null;
		context.commit("SET_DEVICE_TYPE_LOADING", {
			deviceTypeId: type,
			isLoading: true
		});
		await context.dispatch(
			context.getters.deviceTypeModuleName(type) + "/FETCH_ALL_DEVICES",
			{},
			{ root: true }
		);
		context.commit("SET_DEVICE_TYPE_LOADING", {
			deviceTypeId: type,
			isLoading: false
		});
	}
};

const getters = {
	// TODO: current filtering algo is slow due to rebuilding entire device list
	// upon any change to the filter (and therefore redrawing a lot of leaflet
	// markers)
	filteredDevices: (state, getters) => {
		if (_.isEmpty(state.filter)) return null;
		const devices = _.chain(getters.allDevicesByType)
			// .filter((devices, typeKey) => getters.deviceTypes[typeKey].showOnMap)
			.reduce((acc, devices) => Object.assign(acc, devices), {})
			.filter(
				device =>
					(device.deviceId + " " + device.deviceName)
						.toLowerCase()
						.indexOf(state.filter.toLowerCase()) > -1
			)
			.value();
		return devices;
	},

	deviceTypeIconURLByTypeID: (state, getters) => id => {
		const deviceType = getters.deviceTypeById(id);
		if (deviceType && "iconURL" in deviceType) return deviceType.iconURL;
		return null;
	},

	allDevicesByType: (state, getters, rootState, rootGetters) => {
		if (!getters.deviceTypes) return null;
		const devices = _.chain(getters.deviceTypes)
			.pickBy((dt, id) => getters.doesDeviceTypeHaveModule(id) && !dt.disabled)
			.mapValues(
				dt => rootGetters[getters.deviceTypeModuleName(dt.id) + "/allDevices"]
			)
			.pickBy(e => !!e)
			.value();
		return devices;
	},

	devicesOfType: (state, getters, rootState, rootGetters) => type => {
		const mn = getters.deviceTypeModuleName(type);
		if (!mn) return [];
		return rootGetters[mn + "/allDevices"];
	},

	/**
	 * Determines which device types to load
	 * @returns object - ID with value true
	 */
	defaultDeviceTypesToLoad: (state, getters) => {
		let result = {};
		const allDeviceTypes = getters["deviceTypes"];
		for (let index in allDeviceTypes) {
			const deviceType = allDeviceTypes[index];
			result[parseInt(deviceType["id"])] = true;
		}
		return result;
	},

	deviceByTypeAndId: (state, getters, rootState, rootGetters) => ({
		deviceId,
		$deviceTypeId
	}) => {
		if (!deviceId || !$deviceTypeId) return null;
		if (!getters.doesDeviceTypeHaveModule($deviceTypeId)) return null;
		const moduleName = getters.deviceTypeModuleName($deviceTypeId);
		const result = rootGetters[moduleName + "/byIdentifier"](deviceId);
		return result;
	},

	deviceStatusMetadata: (state, getters, rootState, rootGetters) => ({
		deviceId,
		$deviceTypeId
	}) => {
		if (!deviceId || !$deviceTypeId) return null;
		if (!getters.doesDeviceTypeHaveModule($deviceTypeId)) return null;
		const moduleName = getters.deviceTypeModuleName($deviceTypeId);
		const result = rootGetters[moduleName + "/statusMetadata"](deviceId);
		return result;
	},

	deviceTypeById: state => deviceTypeId => {
		if (!state.deviceTypes) return null;
		return state.deviceTypes[deviceTypeId];
	},

	deviceTypeModuleName: (state, getters) => deviceTypeId => {
		const dt = getters.deviceTypeById(deviceTypeId);
		if (dt) return dt.moduleName || dt.shortName.toLowerCase();
		else return false;
	},

	doesDeviceTypeHaveModule: (state, getters) => deviceTypeId =>
		!!getters.deviceTypeModuleName(deviceTypeId) &&
		!getters.deviceTypes[deviceTypeId].disabled,

	// deviceTypeModule: (state, getters) => deviceTypeId => getters.deviceTypeById(deviceTypeId),

	deviceTypes: state => state.deviceTypes,

	filter: state => state.filter,

	contextMenuLatLng: state => state.contextMenuLatLng,

	activeDevice: (state, getters, rootState, rootGetters) => {
		if (!state.deviceTypes) return null;
		if (!getters.doesDeviceTypeHaveModule(state.activeDeviceTypeId))
			return null;
		return rootGetters[
			getters.deviceTypeModuleName(state.activeDeviceTypeId) + "/byIdentifier"
		](state.activeDeviceId);
	},

	isDeviceTypeExpanded: (state, getters) => deviceTypeId =>
		getters.deviceTypes[deviceTypeId].expanded,

	/**
	 * Checks to see if user has role claim for type of device passed in
	 * @param device (object)
	 * @returns bool
	 */
	hasDeviceViewPermission: (
		state,
		getters,
		rootState,
		rootGetters
	) => device => {
		const deviceShortName = getters.deviceTypeById(device.$deviceTypeId)
			.shortName;
		const deviceViewRoleClaim = deviceShortName.toUpperCase() + "view";
		const hasPermission = rootGetters.currentUserHasRole(deviceViewRoleClaim);
		return hasPermission;
	}
};

const initialState = {
	filter: "",
	contextMenuLatLng: null,
	activeDeviceId: null,
	activeDeviceTypeId: null,
	deviceTypes: null
};

const deviceManagerModule = new StoreModule(
	name,
	mutations,
	actions,
	getters,
	initialState
);

export default deviceManagerModule;
